'use client';

import * as Popover from '@radix-ui/react-popover';
import { CheckCircle } from '@ui/components/icon';
import IntersectionTrigger from '@ui/components/intersection-trigger';
import { Button } from '@ui/components/v2/button';
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from '@ui/components/v2/command';
import { wrapClassNames } from '@ui/utils/tailwind';
import type { FC, ReactNode } from 'react';
import { isValidElement, useEffect, useState } from 'react';

function extractText(node: ReactNode): string {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractText).join(' ');
  if (isValidElement<{ children?: ReactNode }>(node)) return extractText(node.props.children);
  return '';
}

import { ChevronDown } from '../icon';
import { Loader } from '../loader';

export type ComboBoxProps = {
  value: string | undefined;
  setValue: (v: string) => any;
  onNextPage?: () => Promise<any>;
  onSearchChange?: (search: string) => void;
  disabledFrontendFilter?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  enableSearch?: boolean;
  optionsClassName?: string;
  popoverContentClassName?: string;
  options: Array<{ label: string | ReactNode; value: string; isDisabled?: boolean; triggerValue?: string }>;
  className?: string;
};
export const Combobox: FC<ComboBoxProps> = ({
  setValue,
  onSearchChange,
  onNextPage,
  disabledFrontendFilter,
  value,
  options,
  placeholder,
  searchPlaceholder,
  optionsClassName = '',
  popoverContentClassName = '',
  disabled,
  enableSearch = true,
  className,
}) => {
  const [open, setOpen] = useState(false);
  const [cachedCurrenOption, setCachedCurrentOption] = useState<{
    label: string | ReactNode;
    value: string;
    triggerValue?: string;
  }>();
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);

  useEffect(() => {
    if (!onSearchChange) return;

    if (!open) onSearchChange('');
  }, [open, onSearchChange]);

  useEffect(() => {
    const selected = options.find(o => o.value === value);
    if (!selected?.label || !value) return;
    if (cachedCurrenOption?.label !== selected?.label || value !== cachedCurrenOption?.value) {
      setCachedCurrentOption({ label: selected?.label, value, triggerValue: selected.triggerValue });
    }
  }, [value, cachedCurrenOption, options]);

  const isCurrentOptionListed = options.some(o => o.value === value);
  return (
    <Popover.Popover onOpenChange={setOpen} open={open}>
      <Popover.Trigger asChild>
        <Button
          disabled={disabled}
          variant="white"
          role="combobox"
          aria-expanded={open}
          className={`w-full h-10 flex justify-between border border-gray-300 text-nowrap text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-200 px-3
              ${value ? '' : 'text-gray-500'} ${className}`}
        >
          <div className="[&>svg]:w-4 flex gap-2 truncate max-w-full">
            {value ? cachedCurrenOption?.triggerValue || cachedCurrenOption?.label : placeholder}
          </div>
          <ChevronDown className="text-typography w-3 h-3 shrink-0" />
        </Button>
      </Popover.Trigger>
      <Popover.Content
        collisionPadding={20}
        className={wrapClassNames(
          `bg-white p-1 shadow-400 rounded-xl relative min-w-[10rem]
          tablet:max-w-sm
          mobile:w-[calc(100vw-40px)]
          ${popoverContentClassName}`
        )}
      >
        <Command
          className={wrapClassNames(`mobile:max-h-52 ${optionsClassName}`)}
          filter={(value, search, keywords) => {
            if (disabledFrontendFilter) return 1;
            if (value.toLowerCase().includes(search.toLowerCase())) return 1;
            if (keywords?.some(key => key.toLowerCase().includes(search.toLowerCase()))) return 1;
            return 0;
          }}
        >
          {enableSearch && <CommandInput onValueChange={onSearchChange} placeholder={searchPlaceholder} />}

          <CommandList>
            {/* <CommandEmpty>No option found.</CommandEmpty> */}
            <CommandGroup>
              {(cachedCurrenOption && !isCurrentOptionListed
                ? [...options, { ...cachedCurrenOption, isDisabled: false }]
                : options
              ).map(o => (
                <CommandItem
                  disabled={o.isDisabled}
                  className="rounded-lg text-sm font-normal text-gray-900 hover:bg-gray-100 selection:bg-gray-100 cursor-pointer"
                  key={o.value}
                  value={o.value}
                  keywords={[extractText(o.label)].filter(Boolean)}
                  onSelect={currentValue => {
                    setValue(currentValue === value ? '' : currentValue);
                    setOpen(false);
                  }}
                >
                  {o.label}
                  <CheckCircle
                    className={wrapClassNames(
                      `ml-auto h-4 w-4 text-blue-500 ${value === o.value ? 'opacity-100' : 'opacity-0'}`
                    )}
                  />
                </CommandItem>
              ))}
              {isFetchingNextPage && <Loader />}
              {onNextPage && (
                <IntersectionTrigger
                  onEnter={async () => {
                    setIsFetchingNextPage(true);
                    await onNextPage();
                    setIsFetchingNextPage(false);
                  }}
                />
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </Popover.Content>
    </Popover.Popover>
  );
};
